import { Auth } from "aws-amplify";
import * as AuthUtils from "utils/auth";
import FetchAction from "../FetchAction";
import UpdateAction from "../UpdateAction";
import * as API from "../../rest/API";
import store from "../ReduxStore";
import { setData2, setCookies, invalidateAll } from "./data.actions";
import { isFetchingUser } from "../selectors/user.selector";
import { moment, SecurityUtils, Utils } from "revlock-webutils";
import config from "cfg/config";
import queryString from "query-string";
import * as ChargebeeUtils from "../../utils/ChargebeeUtils";

const organizationsFetch = () =>
    new FetchAction("ORGANIZATIONS", API.getUserOrganizations);
const organizationUpdate = (remoteAction, reducerType) =>
    new UpdateAction("ORGANIZATIONS", remoteAction, reducerType);

const organizationsUsersFetcher = () =>
    new FetchAction("ORGANIZATIONS_USERS", API.getOrganizationUsers);
const organizationsUsersUpdater = (remoteAction, reducerType) =>
    new UpdateAction(
        "ORGANIZATIONS_USERS",
        remoteAction || API.getOrganizationUsers,
        reducerType
    );

const syncFetcher = () => new FetchAction("SYNC", API.getSyncStatus);
const syncUpdater = (remoteAction, reducerType) =>
    new UpdateAction("SYNC", remoteAction || API.getSyncStatus, reducerType);

const taskFetcher = () => new FetchAction("ALL_TASKS", API.getTaskSummary);

/**
 * Problem (REV-17039): ReduxUrlSync calls setCurrentOrganization from
 * mapStateToProps on every state change. The SSO gate awaits a network call,
 * so current org is not updated yet and the same cross-tenant switch is
 * re-entered thousands of times → pending evaluate-sso-access storms.
 *
 * orgSwitchInFlight: one shared evaluation Promise per target org id while a
 * switch is in progress; concurrent callers join it instead of starting new HTTP.
 */
const orgSwitchInFlight = new Map();

/** TTL cache for sso-enforcement-active so flag-off skips evaluate without re-probing every switch. */
const SSO_ENFORCEMENT_PROBE_TTL_MS = 60 * 1000;
let ssoEnforcementProbeCache = null;

export default class User {
    static signIn = async () => {
        // New session must not inherit a stale sso-enforcement-active probe
        // (e.g. cached active:false skipping evaluate after flag/OrgConfig flip).
        User.clearSsoEnforcementProbeCache();
        User.clearOrgSwitchInFlight();

        const isFetching = isFetchingUser(store.getState());

        if (!isFetching) {
            store.dispatch({
                type: "USER",
                status: "request"
            });
            //mark as start fetching.

            let user, userInfo, serverUser;
            try {
                user = await Auth.currentAuthenticatedUser({
                    bypassCache: true
                });
                serverUser = await API.getUser();
                userInfo = await Auth.currentUserInfo();
            } catch (e) {
                console.error(
                    `Failed to get current authenticated user ${e}`,
                    e.stack
                );
                //when by pass cache is used this method can throw!
            }

            if (user && userInfo && user.attributes && serverUser) {
                user.attributes.email = Utils.normalizeEmail(
                    user.attributes.email
                );
                if (ChargebeeUtils.isChargebeeUser()) {
                    let { isValid } =
                        await ChargebeeUtils.validateChargebeeUiSession();
                    if (!isValid) {
                        localStorage.clear();
                        User.signOut(true);
                        return;
                    }
                }

                // The 'user' object has a username of the underlying user
                // (even if impersonating it will be the super-admin) but their
                // superAdminLevel is from AWS which might be wrong and out of
                // date now that we store it in redis.  So get the user from
                // the server which will have the correct superAdminLevel and
                // if the user is being impersonated will have the actualAdminUser
                // attribute containing the underlying super-admin user.
                const underlyingSuperAdminLevel = serverUser.actualAdminUser
                    ? SecurityUtils.getSuperAdminLevel(
                          serverUser.actualAdminUser
                      )
                    : SecurityUtils.getSuperAdminLevel(serverUser);
                user.superAdminLevel =
                    SecurityUtils.getSuperAdminLevel(serverUser);

                user.lastLogon =
                    user &&
                    user.attributes &&
                    user.attributes["custom:last-logon"];

                user.id = userInfo && userInfo.id;

                if (
                    underlyingSuperAdminLevel >=
                    SecurityUtils.SUPER_ADMIN_LEVEL_1
                ) {
                    const impersonatedUsername =
                        user.attributes["custom:impersonate"];

                    try {
                        if (
                            impersonatedUsername &&
                            impersonatedUsername !== ""
                        ) {
                            const impersonatedUser =
                                await API.getImpersonatedUser(
                                    impersonatedUsername
                                );
                            if (impersonatedUser) {
                                user.attributes = impersonatedUser.attributes;
                                user.id = impersonatedUser.id;
                                user.impersonation = true;
                                user.superAdminLevel =
                                    SecurityUtils.getSuperAdminLevel(
                                        impersonatedUser
                                    );
                            }
                        }
                    } catch (err) {
                        console.error(
                            `Unable to impersonate as [ ${err.toString()}] for ${impersonatedUsername} `
                        );
                    }
                }

                try {
                    user.authorizations = await API.getAuthorizations();
                } catch (err) {
                    console.log(err);
                }

                user.orgs = user.authorizations;

                if (user && user.attributes && user.attributes["custom:data"]) {
                    const data = user.attributes["custom:data"];
                    if (data && data.length > 0) user.data = JSON.parse(data);
                }

                // Per-org user preferences (e.g. reportsV2MigrationModalSeen)
                // so the Reports V2 migration modal can be gated at login.
                user.preferences = serverUser.preferences;

                user.idp = serverUser.idp;
                if (serverUser.idp === "chargebee-idp") {
                    user.isChargebeeUser = true;
                    user.chargebeeIdpType = serverUser.chargebeeIdpType;
                    user.logoutUrl = serverUser.logoutUrl;
                }

                if (user.idp === "revrec-idp") {
                    user.migrateToChargebeeSignIn =
                        serverUser.migrateToChargebeeSignIn;
                }

                store.dispatch({
                    type: "USER",
                    status: "success",
                    user
                });

                if (!user.impersonation)
                    await Auth.updateUserAttributes(user, {
                        "custom:last-logon": moment().format(
                            "MMM, DD YYYY hh:mm a"
                        )
                    });
            } else {
                User.clearLocalStorage();

                await User.signOut();
                //sign out if you cannot get the user or user info
                store.dispatch({
                    type: "NO_USER"
                });
            }
        }
    };

    static clearLocalStorage = () => {
        const signoutMessage = localStorage.getItem("signoutMessage");
        localStorage.clear();
        if (signoutMessage) {
            localStorage.setItem("signoutMessage", signoutMessage);
        }
    };

    static signOut = async (value = true, message) => {
        if (message) {
            localStorage.setItem("signoutMessage", message);
        }

        if (Auth && Auth._oAuthHandler && Auth._oAuthHandler._config) {
            Auth._oAuthHandler._config.redirectSignOut =
                config.AWS.oauth.redirectSignOut;
        }

        //Hack since the oauth handler signout is not set properly automatically
        ChargebeeUtils.removeChargebeeUiSession();
        User.clearSsoEnforcementProbeCache();
        User.clearOrgSwitchInFlight();

        await AuthUtils.signOut({ global: value });
        sessionStorage.clear();
        store.dispatch({
            type: "SIGN_OUT"
        });
    };

    static refreshUser = async (callback) => {
        const user = await Auth.currentAuthenticatedUser({ bypassCache: true });
        user.refreshSession(user.signInUserSession.refreshToken, () => {
            Auth.currentUserCredentials().then(() => {
                User.signIn();
                callback();
            });
        });
    };

    static initDemoOrg(org) {
        organizationUpdate(API.initDemoOrg).doUpdate(false, undefined, org);
    }

    static initProdTest(org) {
        organizationUpdate(API.initProdTest).doUpdate(false, undefined, org);
    }

    static updateOrganizationImage(params, callback, invalidate = true) {
        organizationUpdate(
            API.updateOrganizationImage,
            "updateOrgImg"
        ).doUpdate(invalidate, callback, params);
    }

    static updateOrganization(
        org,
        periodType,
        callback,
        invalidate = false,
        silent = false
    ) {
        if (org.image) delete org.image;

        const action = organizationUpdate(API.updateOrganization);
        if (!silent) action.doUpdate(invalidate, callback, org, periodType);
        else action.doUpdateSilent(invalidate, callback, org, periodType);
    }

    /**
     * Switch current org. For Chargebee IPE users, runs SSO enforcement
     * before applying the switch; on deny redirects to Billing and aborts
     * unless opts.skipBillingRedirect (cold-start walk).
     * Non-Chargebee / flag-off / API errors → existing behavior (allow).
     */
    static async setCurrentOrganization(org, logoutMstr, opts) {
        if (!org || !org.id) {
            return false;
        }

        const current =
            store.getState().currentOrganization &&
            store.getState().currentOrganization.currentOrganization;
        const sameTenant = current && String(current.id) === String(org.id);

        // Cross-tenant only — same-tenant patches (e.g. be_id) skip the gate
        if (!sameTenant && ChargebeeUtils.isChargebeeUser()) {
            const allowed = await User.enforceSsoAccess(org, opts);
            if (!allowed) {
                return false;
            }
        }

        User.applyCurrentOrganization(org, logoutMstr);
        return true;
    }

    /**
     * SSO org-switch gate for Chargebee IPE users.
     *
     * Problem: callers (especially ReduxUrlSync mapStateToProps) re-enter
     * setCurrentOrganization for the same target org while the first call is
     * still awaiting evaluate-sso-access, which stacked thousands of pending
     * API requests and starved other UI calls — even when the feature flag was off.
     *
     * Fix:
     * 1) TTL-cached sso-enforcement-active probe — if inactive for this org,
     *    allow immediately with no evaluate call (preserves pre-SSO behavior).
     * 2) orgSwitchInFlight — concurrent switches to the same orgId share one
     *    evaluation Promise; each caller applies skipBillingRedirect after join.
     * 3) ReduxUrlSync coalesces URL-driven switches via _orgSwitchInFlight.
     */
    static async enforceSsoAccess(org, { skipBillingRedirect = false } = {}) {
        if (!org || !org.id) {
            return true;
        }

        const orgId = String(org.id);

        const enforcementActive = await User.isSsoEnforcementActiveCached([
            org
        ]);
        if (!enforcementActive) {
            return true;
        }

        let evaluationPromise = orgSwitchInFlight.get(orgId);
        if (!evaluationPromise) {
            evaluationPromise = User.evaluateSsoAccessForOrg(org).finally(
                () => {
                    orgSwitchInFlight.delete(orgId);
                }
            );
            orgSwitchInFlight.set(orgId, evaluationPromise);
        }

        const result = await evaluationPromise;
        if (result && result.allowed === false) {
            if (!skipBillingRedirect && result.redirectUrl) {
                // Skip if already returned from Billing (?jwt=) — avoids redirect loop
                const hasJwt = !!queryString.parse(
                    (window.location && window.location.search) || ""
                ).jwt;
                if (!hasJwt) {
                    window.location.href = result.redirectUrl;
                }
            }
            return false;
        }
        return true;
    }

    /**
     * Single evaluate-sso-access for a target org (no redirect).
     * Shared via orgSwitchInFlight so duplicate in-flight switches coalesce.
     */
    static async evaluateSsoAccessForOrg(org) {
        try {
            const result = await API.evaluateSsoAccess({
                tenantId: org.id,
                returnPath: User.ssoReturnPath(org.id),
                uiSession: ChargebeeUtils.getChargebeeUISession()
            });
            if (result && result.allowed === false) {
                return {
                    allowed: false,
                    redirectUrl: result.redirectUrl
                };
            }
        } catch (e) {
            // Fail open — do not block org switch on gate errors
            console.error("SSO access evaluation failed; allowing switch", e);
        }
        return { allowed: true };
    }

    /**
     * Probe: SSO enforcement active for any of the given orgs (config only).
     * Non-Chargebee / errors → false (legacy cold-start pick).
     */
    static async isSsoEnforcementActiveForOrgs(orgs) {
        if (!ChargebeeUtils.isChargebeeUser() || !orgs || !orgs.length) {
            return false;
        }
        try {
            const result = await API.isSsoEnforcementActive({
                tenantIds: orgs.map((o) => o && o.id).filter(Boolean)
            });
            return !!(result && result.active);
        } catch (e) {
            console.error(
                "SSO enforcement probe failed; using legacy org pick",
                e
            );
            return false;
        }
    }

    /**
     * Cached wrapper around isSsoEnforcementActiveForOrgs.
     * Avoids probing on every ReduxUrlSync re-entry when the flag is off.
     */
    static async isSsoEnforcementActiveCached(orgs) {
        const tenantIds = (orgs || [])
            .map((o) => o && o.id)
            .filter(Boolean)
            .map(String)
            .sort()
            .join(",");
        if (!tenantIds) {
            return false;
        }

        const now = Date.now();
        if (
            ssoEnforcementProbeCache &&
            ssoEnforcementProbeCache.key === tenantIds &&
            ssoEnforcementProbeCache.expiresAt > now
        ) {
            return ssoEnforcementProbeCache.active;
        }

        const active = await User.isSsoEnforcementActiveForOrgs(orgs);
        ssoEnforcementProbeCache = {
            key: tenantIds,
            active,
            expiresAt: now + SSO_ENFORCEMENT_PROBE_TTL_MS
        };
        return active;
    }

    /** Clear probe cache (logout / jwt login / tests) so flag flips are picked up. */
    static clearSsoEnforcementProbeCache() {
        ssoEnforcementProbeCache = null;
    }

    /** Clear in-flight org-switch evaluations (tests / logout). */
    static clearOrgSwitchInFlight() {
        orgSwitchInFlight.clear();
    }

    /**
     * Relative path under orgId from ?redirectUrl=, or undefined if missing/unsafe.
     *
     * Use case: Billing returns with
     *   ?clientId=10002&redirectUrl=/10002/dashboards
     * while the browser is still on another tenant (e.g. /10001/...).
     * Without this, SSO deny forward would be /10002 (tenant root) and the
     * user would lose the intended page after re-auth.
     */
    static tenantLocalRedirectFromQuery(orgId) {
        if (!orgId || typeof window === "undefined" || !window.location) {
            return;
        }
        const search = window.location.search || "";
        const params = queryString.parse(search);
        const redirectUrl =
            params && typeof params.redirectUrl === "string"
                ? params.redirectUrl.trim()
                : "";
        if (
            redirectUrl.startsWith("/") &&
            !redirectUrl.startsWith("//") &&
            !redirectUrl.includes("://") &&
            (redirectUrl === `/${orgId}` ||
                redirectUrl.startsWith(`/${orgId}/`) ||
                redirectUrl.startsWith(`/${orgId}?`))
        ) {
            return redirectUrl;
        }
    }

    /**
     * Path for Billing forward on SSO deny.
     * Prefers tenant-local ?redirectUrl= (see tenantLocalRedirectFromQuery);
     * else current pathname if already on tenant; else tenant root.
     * Pathname only — never append ?jwt= (Billing adds jwt on return).
     */
    static ssoReturnPath(orgId) {
        const fromQuery = User.tenantLocalRedirectFromQuery(orgId);
        if (fromQuery) {
            return fromQuery;
        }

        const path =
            (typeof window !== "undefined" &&
                window.location &&
                window.location.pathname) ||
            "";
        if (path === `/${orgId}` || path.startsWith(`/${orgId}/`)) {
            return path;
        }
        return `/${orgId}`;
    }

    static applyCurrentOrganization(org, logoutMstr) {
        invalidateAll();
        store.dispatch({
            type: "RESET_ORG",
            status: "success"
        });

        if (logoutMstr) {
            setData2("doMstrLogout", true);
        }

        if (window.HotGlue) {
            window.HotGlue.setTenant && window.HotGlue.setTenant(org.id);
        }

        setCookies("orgId", org.id, { path: "/" });

        store.dispatch({
            type: "CURRENT_ORGANIZATION",
            status: "success",
            response: org
        });
    }

    static inviteUserForOrganization(params, callback) {
        organizationsUsersUpdater(
            API.inviteUserForOrganization,
            "invitedUser"
        ).doUpdate(true, callback, params);
    }

    static removeUserFromOrganization(
        user,
        params,
        callback,
        invalidate = true
    ) {
        organizationsUsersUpdater(
            API.removeUserFromOrganization,
            "removeUser"
        ).doUpdate(invalidate, callback, params);

        // figureout if user opened sso for signup process
        const queryParams =
            (window.location.search &&
                queryString.parse(window.location.search)) ||
            {};
        const cbsso =
            (queryParams.cbssoredirect &&
                queryParams.cbssoredirect === "true") ||
            (window.location.origin.match(/-sso.revrec/) &&
                window.location.origin.match(/-sso.revrec/).length > 0);

        store.dispatch({
            type: "ORGANIZATIONS_USERS_REMOVE",
            data: user,
            cbsso
        });
    }

    static updateOrganizationUser(params, callback) {
        organizationsUsersUpdater(
            API.updateOrganizationUser,
            "updateUserRole"
        ).doUpdate(true, callback, params);
    }

    static fetchCurrentOrganzationUsers(state, stateFilter, ...args) {
        return organizationsUsersFetcher().doFetch(state, stateFilter, ...args);
    }

    static fetchUserOrganizations(state, stateFilter, ...args) {
        return organizationsFetch().doFetch(state, stateFilter, ...args);
    }

    static getOrganizationImage(orgs, callback) {
        return organizationUpdate(API.getOrgImages, "OrgsImages").doUpdate(
            false,
            callback,
            orgs
        );
    }

    /**
     * Task Actions
     */
    static fetchAllTasks(state, stateFilter, ...args) {
        return taskFetcher().doFetch(state, stateFilter, ...args);
    }

    /**
     * Sync Actions
     */
    static fetchSync(state, stateFilter, ...args) {
        return syncFetcher().doFetch(state, stateFilter, ...args);
    }

    static syncStatus(params, callback) {
        syncUpdater().doUpdate(false, callback, params);
    }

    static triggerSync(params, callback, invalidate = false) {
        syncUpdater(API.triggerSync, "triggerSync").doUpdate(
            invalidate,
            callback,
            params
        );
    }

    static invalidateSync() {
        store.dispatch({
            type: "INVALIDATE_SYNC"
        });
    }

    static invalidateOrganizationUsers = () => {
        store.dispatch({
            type: "ORGANIZATION_USERS_INVALIDATE"
        });
    };

    static invalidateOrganization = () => {
        store.dispatch({
            type: "ORGANIZATION_INVALIDATE"
        });
    };
}
